import { useState, useEffect } from 'react';
import { toJpeg } from 'html-to-image';
import {
  Button,
  ButtonSet,
  Slider,
  Tooltip,
  ModalWrapper,
  Accordion,
  AccordionItem,
  InlineLoading,
  InlineLoadingStatus,
  TextInput,
} from 'carbon-components-react';
import { Alignments } from '@carbon/charts/interfaces/enums';
import { FormattedMessage, useIntl } from 'react-intl';
import Cookies from 'js-cookie';
import './MASROIEstimator.scss';
import Footnote from './Footnote';
import { DocumentPdf32, PdfReference16, Reset16 } from '@carbon/icons-react';
import '@carbon/charts/styles.css';
import 'src/common/style/cds-data.scss';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import { segmentTracking } from '../lib/tracker';
import {
  removeSymbol,
  useCurrency,
} from 'src/common/hooks/widgets/useCurrency';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import * as URX_FORM from '../constants/urx-form';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { DonutChart } from '@carbon/charts-react';

import {
  MaintenanceFTEs,
  MaintenancePersonProd,
  MaintenanceAnnualFTERate,
  ExtMaintenanceVendSpend,
  ExtMaintenanceVendSpendReduce,
  DownTimeHours,
  DownTimeReduce,
  DownTimeCostPerHour,
  ReplaceAssetBudget,
  AssetsLifeExtension,
  MROSpend,
  MroOverSpendReduce,
  AssetMgmtToolCost,
  AssetMgmtCostReduce,
  SKELETON_IMAGE,
  UT30MasROI,
} from '../constants/defaults';
import messages from '../locales/messages';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { setPrice as setROI } from '../hooks/useRoi';
import {
  determineCountry,
  LangDefaults,
  useLang,
} from 'src/common/hooks/widgets/useLang';
import countryList from '../data/currencies.json';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { FormModal } from 'src/common/utils/FormModal';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { Helmet } from 'react-helmet';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { PDFGenerate } from './PDFGenerate';

export interface MASROIEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

export interface inputType {
  maintenanceFTEs: number;
  maintenancePersonProd: number;
  maintenanceAnnualFTERate: number;
  extMaintenanceVendSpend: number;
  extMaintenanceVendSpendReduce: number;
  downTimeHours: number;
  downTimeReduce: number;
  downTimeCostPerHour: number;
  replaceAssetBudget: number;
  assetsLifeExtension: number;
  mroSpend: number;
  mroOverSpendReduce: number;
  assetMgmtToolCost: number;
  assetMgmtCostReduce: number;
}

export const inputDefault: inputType = {
  maintenanceFTEs: MaintenanceFTEs.default,
  maintenancePersonProd: MaintenancePersonProd.default,
  maintenanceAnnualFTERate: MaintenanceAnnualFTERate.default,
  extMaintenanceVendSpend: ExtMaintenanceVendSpend.default,
  extMaintenanceVendSpendReduce: ExtMaintenanceVendSpendReduce.default,
  downTimeHours: DownTimeHours.default,
  downTimeReduce: DownTimeReduce.default,
  downTimeCostPerHour: DownTimeCostPerHour.default,
  replaceAssetBudget: ReplaceAssetBudget.default,
  assetsLifeExtension: AssetsLifeExtension.default,
  mroSpend: MROSpend.default,
  mroOverSpendReduce: MroOverSpendReduce.default,
  assetMgmtToolCost: AssetMgmtToolCost.default,
  assetMgmtCostReduce: AssetMgmtCostReduce.default,
};

export interface MaxMin {
  maintenanceFTEsMax: boolean;
  maintenanceFTEsMin: boolean;
  maintenancePersonProdMax: boolean;
  maintenancePersonProdMin: boolean;
  maintenanceAnnualFTERateMax: boolean;
  maintenanceAnnualFTERateMin: boolean;
  extMaintenanceVendSpendMax: boolean;
  extMaintenanceVendSpendMin: boolean;
  extMaintenanceVendSpendReduceMax: boolean;
  extMaintenanceVendSpendReduceMin: boolean;
  downTimeHoursMax: boolean;
  downTimeHoursMin: boolean;
  downTimeReduceMax: boolean;
  downTimeReduceMin: boolean;
  downTimeCostPerHourMax: boolean;
  downTimeCostPerHourMin: boolean;
  replaceAssetBudgetMax: boolean;
  replaceAssetBudgetMin: boolean;
  assetsLifeExtensionMax: boolean;
  assetsLifeExtensionMin: boolean;
  mroSpendMax: boolean;
  mroSpendMin: boolean;
  mroOverSpendReduceMax: boolean;
  mroOverSpendReduceMin: boolean;
  assetMgmtToolCostMax: boolean;
  assetMgmtToolCostMin: boolean;
  assetMgmtCostReduceMax: boolean;
  assetMgmtCostReduceMin: boolean;
}

export const MaxMinObject: MaxMin = {
  maintenanceFTEsMax: false,
  maintenanceFTEsMin: false,
  maintenancePersonProdMax: false,
  maintenancePersonProdMin: false,
  maintenanceAnnualFTERateMax: false,
  maintenanceAnnualFTERateMin: false,
  extMaintenanceVendSpendMax: false,
  extMaintenanceVendSpendMin: false,
  extMaintenanceVendSpendReduceMax: false,
  extMaintenanceVendSpendReduceMin: false,
  downTimeHoursMax: false,
  downTimeHoursMin: false,
  downTimeReduceMax: false,
  downTimeReduceMin: false,
  downTimeCostPerHourMax: false,
  downTimeCostPerHourMin: false,
  replaceAssetBudgetMax: false,
  replaceAssetBudgetMin: false,
  assetsLifeExtensionMax: false,
  assetsLifeExtensionMin: false,
  mroSpendMax: false,
  mroSpendMin: false,
  mroOverSpendReduceMax: false,
  mroOverSpendReduceMin: false,
  assetMgmtToolCostMax: false,
  assetMgmtToolCostMin: false,
  assetMgmtCostReduceMax: false,
  assetMgmtCostReduceMin: false,
};

export interface roiCookieContent {
  MaintenanceTotal: string;
  ReduceExtMaintTotal: string;
  ReduceDowntimeCostTotal: string;
  ExtendAssetLifeTotal: string;
  IncreaseMROTotal: string;
  AssetMgmtToolsTotal: string;
  estimate: string;
  summaryImage: string;
  resultsImage: string;
  isPdf: boolean;
  input: inputType;
}

export const cookieContentObject: roiCookieContent = {
  MaintenanceTotal: '',
  ReduceExtMaintTotal: '',
  ReduceDowntimeCostTotal: '',
  ExtendAssetLifeTotal: '',
  IncreaseMROTotal: '',
  AssetMgmtToolsTotal: '',
  estimate: '',
  summaryImage: '',
  resultsImage: '',
  isPdf: false,
  input: inputDefault,
};

const MASROIEstimator = ({
  langCode,
  test,
  countryToTest,
}: MASROIEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [urxlang] = useState(localeCode.toString());
  const [input, setInput] = useState(inputDefault);
  const [invalidMaxMin, setInvalidMaxMin] = useState(MaxMinObject);
  const [cookieContentROI, setCookieContentROI] = useState(cookieContentObject);
  const [pdfExport, setPdfExport] = useState(false);
  const isSmall = useSmallBreakpoint('560px');
  const [currency, setCurrency] = useState('');
  const [, setCountry] = useState('');
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );
  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  // Set USD
  const [, setModalLocalCurrency] = useState(LangDefaults.localCurrency);
  // Set English language
  const [modalLocalLanguage, setModalLocalLanguage] = useState(
    LangDefaults.CountryLanguage,
  );
  const [MaintenanceTotal, setMaintenanceTotal] = useCurrency(
    'USD',
    'us-en',
    0,
  );
  const [ReduceExtMaintTotal, setReduceExtMaintTotal] = useCurrency(
    'USD',
    'us-en',
    0,
  );
  const [ReduceDowntimeCostTotal, setReduceDowntimeCostTotal] = useCurrency(
    'USD',
    'us-en',
    0,
  );
  const [ExtendAssetLifeTotal, setExtendAssetLifeTotal] = useCurrency(
    'USD',
    'us-en',
    0,
  );
  const [IncreaseMROTotal, setIncreaseMROTotal] = useCurrency(
    'USD',
    'us-en',
    0,
  );
  const [AssetMgmtToolsTotal, setAssetMgmtToolsTotal] = useCurrency(
    'USD',
    'us-en',
    0,
  );
  const [estimatedSavings, setEstimatedSavings] = useCurrency(
    'USD',
    'us-en',
    0,
  );
  const [exportPdfStatus, setExportPdfStatus] = useState(false);
  const [loaderStatus, setLoaderStaus] = useState(
    'active' as InlineLoadingStatus,
  );
  const [loaderDes, setLoaderDesc] = useState(
    <FormattedMessage {...messages.pdfGenerated} />,
  );
  const [isPdf, changeIsPdf] = useState(false);
  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [environment] = useEnv();
  const [formComplete, setFormComplete] = useState(false);
  const { bpName, logo, companyId } = useBPName(UT30MasROI, true);
  const [dataInputTitles, setDataInputTitles] = useState(['']);
  const intl = useIntl();

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
    test,
    localeCode,
  );

  /**
   * Call to start the pricing
   */
  setROI(
    input,
    setMaintenanceTotal,
    setReduceExtMaintTotal,
    setReduceDowntimeCostTotal,
    setExtendAssetLifeTotal,
    setIncreaseMROTotal,
    setAssetMgmtToolsTotal,
    setEstimatedSavings,
  );

  /**
   * Define objects required for graph
   */
  const state = {
    data: [
      {
        group: intl.formatMessage(messages.accordionTitle_1),
        value: Number(removeSymbol('USD', MaintenanceTotal).replace(/,/g, '')),
      },
      {
        group: intl.formatMessage(messages.accordionTitle_2),
        value: Number(
          removeSymbol('USD', ReduceExtMaintTotal).replace(/,/g, ''),
        ),
      },
      {
        group: intl.formatMessage(messages.accordionTitle_3),
        value: Number(
          removeSymbol('USD', ReduceDowntimeCostTotal).replace(/,/g, ''),
        ),
      },
      {
        group: intl.formatMessage(messages.accordionTitle_4),
        value: Number(removeSymbol('USD', IncreaseMROTotal).replace(/,/g, '')),
      },
      {
        group: intl.formatMessage(messages.accordionTitle_5),
        value: Number(
          removeSymbol('USD', ExtendAssetLifeTotal).replace(/,/g, ''),
        ),
      },
      {
        group: intl.formatMessage(messages.accordionTitle_6),
        value: Number(
          removeSymbol('USD', AssetMgmtToolsTotal).replace(/,/g, ''),
        ),
      },
    ],
    options: {
      resizable: true,
      legend: {
        alignment: Alignments.LEFT,
        clickable: false,
        truncation: {
          type: 'none',
        },
      },
      donut: {
        alignment: Alignments.CENTER,
      },
      height: '400px',
      toolbar: {
        enabled: false,
      },
    },
  };

  /**
   * PDF generation
   */
  const [resultsImage, setResultsImage] = useState(SKELETON_IMAGE);
  const [summaryImage, setSummaryImage] = useState(SKELETON_IMAGE);
  const [fetchPriceStatus, setFetchPriceStatus] = useState('');
  const [fetchSummaryStatus, setFetchSummaryStatus] = useState('');
  const [conversionFired, setConversionFired] = useState(false);
  const [loadInline, setLoadInline] = useState(false);
  const fetchImagesFromHTMLElement = async (id: string, bgColor: string) => {
    const div = document.getElementById(id) as HTMLElement;
    return toJpeg(div, { backgroundColor: bgColor });
  };
  const prepareResultsForPDF = async () => {
    if (!conversionFired) {
      setConversionFired(true);
      setLoaderDesc(<FormattedMessage {...messages.pdfGenerated} />);
      setLoadInline(true);
      await fetchImagesFromHTMLElement('graphSummary', '#fff')
        .then((dataUrl) => {
          setResultsImage(dataUrl);
          setFetchPriceStatus('success');
        })
        .catch((error) => {
          console.error(
            'oops, something went wrong while fetching the images for results',
            error,
          );
          setFetchPriceStatus('error');
        });

      await fetchImagesFromHTMLElement('tableSummary', '#f4f4f4')
        .then((dataUrl) => {
          setSummaryImage(dataUrl);
          setFetchSummaryStatus('success');

          setLoaderStaus('finished' as InlineLoadingStatus);
        })
        .catch((error) => {
          console.error(
            'oops, something went wrong while fetching the images for summary',
            error,
          );
          setFetchSummaryStatus('error');
        });
    }
  };

  useEffect(() => {
    if (isPdf) {
      prepareResultsForPDF();
    }
    if (conversionFired) {
      if (fetchPriceStatus === 'error' || fetchSummaryStatus === 'error') {
        setLoaderStaus('error' as InlineLoadingStatus);
        setLoaderDesc(<FormattedMessage {...messages.pdfError} />);
      } else if (
        fetchPriceStatus === 'success' &&
        fetchSummaryStatus === 'success'
      ) {
        setLoaderDesc(<FormattedMessage {...messages.pdfCompleted} />);
        setExportPdfStatus(false);
        handleContent();
        setPdfExport(true);
      }
    }
  }, [exportPdfStatus, fetchPriceStatus, fetchSummaryStatus, conversionFired]);

  /**
   * Define validation of the Input field of the sliders
   */
  useEffect(() => {
    const mfteId = document.getElementById('MFTE-Id-input-for-slider');
    const mppId = document.getElementById('MPP-Id-input-for-slider');
    const mfterId = document.getElementById('MFTER-Id');
    const emvsId = document.getElementById('EMVS-Id');
    const emvsrId = document.getElementById('EMVSR-Id-input-for-slider');
    const dtId = document.getElementById('DT-Id-input-for-slider');
    const dtrId = document.getElementById('DTR-Id-input-for-slider');
    const dtcphId = document.getElementById('DTCPH-Id');
    const rabId = document.getElementById('RAB-Id');
    const aleId = document.getElementById('ALE-Id-input-for-slider');
    const mroId = document.getElementById('MRO-Id');
    const mrorId = document.getElementById('MROR-Id-input-for-slider');
    const amtcId = document.getElementById('AMTC-Id');
    const amcrId = document.getElementById('AMCR-Id-input-for-slider');

    const addListener = (
      id: HTMLElement | null,
      name: string,
      max: number,
      min: number,
    ) => {
      if (id !== null) {
        id.addEventListener('input', (e) => {
          if (e != null) {
            const element = e.currentTarget as HTMLInputElement;
            if (element.value !== '') {
              if (Number.parseInt(element.value) > max) {
                element.value = max.toString();
                setInvalidMaxMin({
                  ...invalidMaxMin,
                  [name + 'Max']: true,
                });
                setInput({
                  ...input,
                  [name]: max,
                });
              } else if (Number.parseInt(element.value) < min) {
                element.value = min.toString();
                setInvalidMaxMin({
                  ...invalidMaxMin,
                  [name + 'Min']: true,
                });
                setInput({
                  ...input,
                  [name]: min,
                });
              }
            } else {
              element.value = ' ';
            }
          }
        });
      }
    };

    /**
     * Current Number of Maintenance Personnel (Full-Time-Equivalents)
     */
    addListener(
      mfteId,
      'maintenanceFTEs',
      MaintenanceFTEs.max,
      MaintenanceFTEs.min,
    );

    /**
     * Current Estimated Maintenance Personnel Productivity Improvement
     */
    addListener(
      mppId,
      'maintenancePersonProd',
      MaintenancePersonProd.max,
      MaintenancePersonProd.min,
    );

    /**
     * Current Average Maintenance Personnel - Annual Cost Rate
     */
    addListener(
      mfterId,
      'maintenanceAnnualFTERate',
      MaintenanceAnnualFTERate.max,
      MaintenanceAnnualFTERate.min,
    );
    /**
     * Current External Maintenance Vendor Spend
     */
    addListener(
      emvsId,
      'extMaintenanceVendSpend',
      ExtMaintenanceVendSpend.max,
      ExtMaintenanceVendSpend.min,
    );
    /**
     * Current % External Maintenance Vendor Spend Reduction
     */
    addListener(
      emvsrId,
      'extMaintenanceVendSpendReduce',
      ExtMaintenanceVendSpendReduce.max,
      ExtMaintenanceVendSpendReduce.min,
    );
    /**
     * Current Annual Downtime Hours
     */
    addListener(dtId, 'downTimeHours', DownTimeHours.max, DownTimeHours.min);
    /**
     * Current % Downtime Reduction
     */
    addListener(
      dtrId,
      'downTimeReduce',
      DownTimeReduce.max,
      DownTimeReduce.min,
    );
    /**
     * Current Average Economic Cost of Downtime per hour
     */
    addListener(
      dtcphId,
      'downTimeCostPerHour',
      DownTimeCostPerHour.max,
      DownTimeCostPerHour.min,
    );
    /**
     * Current Annual CapEx Budget to Replace/Overhaul Assets
     */
    addListener(
      rabId,
      'replaceAssetBudget',
      ReplaceAssetBudget.max,
      ReplaceAssetBudget.min,
    );
    /**
     * Current % Reduction in CapEx Budget Need
     */
    addListener(
      aleId,
      'assetsLifeExtension',
      AssetsLifeExtension.max,
      AssetsLifeExtension.min,
    );
    /**
     * Current Annual MRO Spend
     */
    addListener(mroId, 'mroSpend', MROSpend.max, MROSpend.min);

    /**
     * Current % MRO Spend Reduction
     */
    addListener(
      mrorId,
      'mroOverSpendReduce',
      MroOverSpendReduce.max,
      MroOverSpendReduce.min,
    );

    /**
     * Current Annual Cost to Support Current Asset Mgmt Tools
     */
    addListener(
      amtcId,
      'assetMgmtToolCost',
      AssetMgmtToolCost.max,
      AssetMgmtToolCost.min,
    );

    /**
     * Current % Reduction of Asset Mgmt Tools Support Cost
     */
    addListener(
      amcrId,
      'assetMgmtCostReduce',
      AssetMgmtCostReduce.max,
      AssetMgmtCostReduce.min,
    );
  });

  useEffect(() => {
    setCookie();
  }, [
    input,
    MaintenanceTotal,
    ReduceExtMaintTotal,
    ReduceDowntimeCostTotal,
    ExtendAssetLifeTotal,
    IncreaseMROTotal,
    AssetMgmtToolsTotal,
    estimatedSavings,
  ]);

  const setCookie = () => {
    const cookieContent =
      ' Number of Maintenance Personnel (Full-Time-Equivalents):' +
      input.maintenanceFTEs +
      ' | Estimated Maintenance Personnel Productivity Improvement:' +
      input.maintenancePersonProd +
      ' | Average Maintenance Personnel - Annual Cost Rate:' +
      input.maintenanceAnnualFTERate +
      '| External Maintenance Vendor Spend: ' +
      input.extMaintenanceVendSpend +
      ' | % External Maintenance Vendor Spend Reduction: ' +
      input.extMaintenanceVendSpendReduce +
      ' | Annual Downtime Hours: ' +
      input.downTimeHours +
      ' | % Downtime Reduction: ' +
      input.downTimeReduce +
      ' | Average Economic Cost of Downtime per hour: ' +
      input.downTimeCostPerHour +
      ' | Annual CapEx Budget to Replace/Overhaul Assets: ' +
      input.replaceAssetBudget +
      ' | % Reduction in CapEx Budget Need: ' +
      input.assetsLifeExtension +
      ' | Annual MRO Spend: ' +
      input.mroSpend +
      ' | % MRO Spend Reduction: ' +
      input.mroOverSpendReduce +
      ' | Annual Cost to Support Current Asset Mgmt Tools: ' +
      input.assetMgmtToolCost +
      ' | % Reduction of Asset Mgmt Tools Support Cost: ' +
      input.assetMgmtCostReduce +
      ' | Increase maintenance productivity: ' +
      MaintenanceTotal +
      ' | Reduce external maintenance spend: ' +
      ReduceExtMaintTotal +
      ' | Reduce downtime cost: ' +
      ReduceDowntimeCostTotal +
      ' | Extend useful life of assets: ' +
      ExtendAssetLifeTotal +
      ' | Reduce MRO parts and supplies cost: ' +
      IncreaseMROTotal +
      ' | Rationalize asset management tools: ' +
      AssetMgmtToolsTotal +
      ' | Estimated benefits: ' +
      estimatedSavings;
    const text = JSON.stringify({
      source: URX_FORM.FORM_ID,
      // One field to provide all values (stringify)
      dcq: {
        Q_NOTESDATA:
          cookieContent +
          ' | Request a quote -IBM MAS Benefits Estimator-' +
          ' | FormId:' +
          URX_FORM.FORM_ID,
        Q_ASSET_NAME: 'IBM MAS Benefits Estimator', // ISC - Asset Name
      },
    });
    Cookies.set('urxdcq', text);
  };

  const handleContent = () => {
    const contentROI = {
      content: {
        isPdf,
        summaryImage,
        resultsImage,
        input,
        MaintenanceTotal,
        ReduceExtMaintTotal,
        ReduceDowntimeCostTotal,
        ExtendAssetLifeTotal,
        IncreaseMROTotal,
        AssetMgmtToolsTotal,
        estimate: estimatedSavings,
      },
    };
    setCookieContentROI(contentROI.content);
  };

  useEffect(() => {
    if (pdfExport) {
      const dataInputTitles = [
        intl.formatMessage(messages.maintenanceFTEsLabel),
        intl.formatMessage(messages.maintenancePersonProdLabel),
        intl.formatMessage(messages.maintenancePersonFTERateLabel),
        intl.formatMessage(messages.extMaintenanceVendorSpendLabel),
        intl.formatMessage(messages.extMaintVendorSpendReduceLabel),
        intl.formatMessage(messages.downTimeHoursAnnualLabel),
        intl.formatMessage(messages.downTimeReduceLabel),
        intl.formatMessage(messages.downTimePerHrCostLabel),
        intl.formatMessage(messages.annualMroSpendLabel),
        intl.formatMessage(messages.mroOverSpendReduceLabel),
        intl.formatMessage(messages.replaceAssetsBudgetLabel),
        intl.formatMessage(messages.lifeAssetsExtLabel),
        intl.formatMessage(messages.asstMgmtToolsCostLabel),
        intl.formatMessage(messages.asstMgmtToolsCostReduceLabel),
        intl.formatMessage(messages.accordionTitle_1),
        intl.formatMessage(messages.accordionTitle_2),
        intl.formatMessage(messages.accordionTitle_3),
        intl.formatMessage(messages.accordionTitle_4),
        intl.formatMessage(messages.accordionTitle_5),
        intl.formatMessage(messages.accordionTitle_6),
        intl.formatMessage(messages.disclaimer_text),
      ];
      setDataInputTitles(dataInputTitles);
    }
  }, [pdfExport]);

  const props = {
    imgSummary: '',
    imgSummary1: '',
    cookieContent: cookieContentROI,
    dataInputTitles,
    currency,
    userLocale,
  };

  if (cookieContentROI) {
    props.imgSummary = cookieContentROI.summaryImage;
    props.imgSummary1 = cookieContentROI.resultsImage;
  }

  const pdfObject = PDFGenerate(props);
  const pdfThanksContent = [
    {
      group: String(intl.formatMessage(messages['downloadText'])),
      value: `mas_bva_${new Date()}.pdf`,
    },
  ];

  const handleSliders = (
    value: number,
    max: number,
    min: number,
    name: string,
  ) => {
    if (value >= min && value <= max) {
      setInput({
        ...input,
        [name]: value,
      });
      setInvalidMaxMin({
        ...invalidMaxMin,
        [name + 'Max']: false,
        [name + 'Min']: false,
      });
    } else if (value > max) {
      setInvalidMaxMin({
        ...invalidMaxMin,
        [name + 'Max']: true,
      });
      setInput({
        ...input,
        [name]: max,
      });
    } else {
      setInvalidMaxMin({
        ...invalidMaxMin,
        [name + 'Min']: true,
      });
      setInput({
        ...input,
        [name]: min,
      });
    }
  };

  return (
    <div
      className="MAS_ROI widget-styles"
      dir={localeCode.includes('ar') ? 'rtl' : 'ltr'}
    >
      <Helmet>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/loader.js`}
        ></script>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/main.js`}
        ></script>
      </Helmet>
      <div className="bx--grid" id="MASGrid">
        <div className="bx--row mar3">
          <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
            <div className="bx--row">
              <div className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12">
                {localExist && toggleCountry ? (
                  <ModalWrapper
                    modalLabel={`Found country ${modalLocalCountry}. Access page in ${modalLocalLanguage}`}
                    modalHeading={`The MAS ROI is available in ${modalLocalLanguage} `}
                    size={'sm'}
                    primaryButtonText={'Cool! Take me there'}
                    secondaryButtonText={'Stay here'}
                    buttonTriggerText={`Access page in ${modalLocalLanguage}`}
                    handleOpen={() => {
                      segmentTracking('CTA Clicked', {
                        pageTitle: 'MAS Pricing Page',
                        object: 'Pricing page',
                        resultValue: 'CTA clicked',
                        CTA: 'Modal open ' + modalLocalCountry,
                        location: 'ROI Estimator',
                        action: '',
                        field: '',
                      });
                    }}
                    handleSubmit={() => {
                      localStorage.setItem('langLocale', selectedLocale);
                      (window.location.href =
                        'https://www.ibm.com/' +
                        selectedLocale +
                        '/products/maximo/pricing'),
                        segmentTracking('CTA Clicked', {
                          pageTitle: 'MAS Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Switched to country ' + modalLocalCountry,
                          location: 'ROI Estimator',
                          action: '',
                          field: '',
                        });
                      return true;
                    }}
                    onSecondarySubmit={() => {
                      segmentTracking('CTA Clicked', {
                        pageTitle: 'QRadar Landing Page',
                        object: 'Pricing page',
                        resultValue: 'CTA clicked',
                        CTA: 'Stay here button clicked ' + modalLocalCountry,
                        location: 'ROI Estimator',
                        action: '',
                        field: '',
                      });
                      return true;
                    }}
                  ></ModalWrapper>
                ) : null}
              </div>
            </div>
            {/*  Page titles */}
            <div className="bx--row">
              <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16">
                <div id="pricing">
                  <div className="bx--row mar1">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_sub_heading">
                        <FormattedMessage {...messages.widgetText_1} />
                      </p>
                    </div>
                  </div>
                  <div className="bx--row mar1">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_body_2">
                        <FormattedMessage {...messages.widgetInfo} />{' '}
                        <a
                          target="_blank"
                          href="https://www.ibm.com/downloads/cas/WRD4G1AN"
                        >
                          <FormattedMessage {...messages.caseStudy} />
                        </a>
                        {'. '}
                        <FormattedMessage {...messages.widgetInfo2} />
                      </p>
                    </div>
                  </div>
                  {/*  Advanced Options*/}
                  <div className="bx--row mar1">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Button
                        className="alignright"
                        renderIcon={Reset16}
                        kind="tertiary"
                        onClick={() => {
                          setExportPdfStatus(false);
                          changeIsPdf(false);
                          setConversionFired(false);
                          setLoadInline(false);
                          setInput(inputDefault);
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'MAS ROI Page',
                            object: 'MAS ROI Page',
                            resultValue: 'CTA clicked',
                            CTA: 'Start Over',
                            location: 'MAS ROI Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                      >
                        <FormattedMessage {...messages.startOver} />
                      </Button>
                    </div>
                  </div>
                  <div className="bx--row mar1">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Accordion size={'lg'}>
                        <AccordionItem
                          open={true}
                          title={
                            <p className="page_body_2">
                              <FormattedMessage
                                {...messages.accordionTitle_1}
                              />
                            </p>
                          }
                        >
                          {/*  Number of Maintenance Personnel (Full-Time-Equivalents) title*/}
                          <div className="bx--row mar3 expand-description">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <p className="slide_text">
                                <FormattedMessage
                                  {...messages.accordionDesc_1}
                                />
                              </p>
                            </div>
                          </div>
                          <div className="bx--row mar3">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Tooltip
                                onChange={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Maintenance FTEs tooltip',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={isSmall ? 'left' : 'bottom'}
                                tabIndex={0}
                                triggerText={
                                  <span className="page_body">
                                    <FormattedMessage
                                      {...messages.maintenanceFTEsLabel}
                                    />
                                  </span>
                                }
                              >
                                <p className="slide_text">
                                  <FormattedMessage
                                    {...messages.maintenanceFTEsDescTootlip}
                                  />
                                </p>
                              </Tooltip>
                            </div>
                          </div>
                          <div className="bx--row expand-slider">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              {/* Number of Maintenance Personnel (Full-Time-Equivalents) Slider */}
                              <Slider
                                id="MFTE-Id"
                                name="MFTEMaxMin"
                                min={MaintenanceFTEs.min}
                                max={MaintenanceFTEs.max}
                                value={input.maintenanceFTEs}
                                inputType={'number'}
                                onBlur={() => {
                                  null;
                                }}
                                invalid={
                                  invalidMaxMin.maintenanceFTEsMax ||
                                  invalidMaxMin.maintenanceFTEsMin
                                }
                                labelText={
                                  invalidMaxMin.maintenanceFTEsMax ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_1}
                                      />
                                      {` ${MaintenanceFTEs.max}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : invalidMaxMin.maintenanceFTEsMin ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_3}
                                      />
                                      {` ${MaintenanceFTEs.min}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : null
                                }
                                onChange={({ value }) => {
                                  handleSliders(
                                    value,
                                    MaintenanceFTEs.max,
                                    MaintenanceFTEs.min,
                                    'maintenanceFTEs',
                                  );

                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Maintenance FTE slider',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          </div>
                          {/*  Estimated Maintenance Personnel Productivity Improvement */}
                          <div className="bx--row mar3">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Tooltip
                                onChange={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Maintenance person productivity tooltip',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={'bottom'}
                                tabIndex={0}
                                triggerText={
                                  <span className="page_body">
                                    <FormattedMessage
                                      {...messages.maintenancePersonProdLabel}
                                    />
                                  </span>
                                }
                              >
                                <p className="slide_text">
                                  <FormattedMessage
                                    {...messages.maintenancePersonProdDescTooltip}
                                  />
                                </p>
                              </Tooltip>
                            </div>
                          </div>
                          {/* Estimated Maintenance Personnel Productivity Improvement Slider */}
                          <div className="bx--row expand-slider">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Slider
                                id="MPP-Id"
                                min={MaintenancePersonProd.min}
                                max={MaintenancePersonProd.max}
                                inputType={'number'}
                                value={input.maintenancePersonProd}
                                invalid={
                                  invalidMaxMin.maintenancePersonProdMax ||
                                  invalidMaxMin.maintenancePersonProdMin
                                }
                                labelText={
                                  invalidMaxMin.maintenancePersonProdMax ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_1}
                                      />
                                      {` ${MaintenancePersonProd.max}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : invalidMaxMin.maintenancePersonProdMin ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_3}
                                      />
                                      {` ${MaintenancePersonProd.min}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : null
                                }
                                onBlur={() => {
                                  null;
                                }}
                                onChange={({ value }) => {
                                  handleSliders(
                                    value,
                                    MaintenancePersonProd.max,
                                    MaintenancePersonProd.min,
                                    'maintenancePersonProd',
                                  );

                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Maintenance Person prod slider',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          </div>
                          {/*  Average Maintenance Personnel - Annual Cost Rate  */}
                          <div className="bx--row mar3">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Tooltip
                                onChange={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Maintenance FTE Rate tooltip',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={isSmall ? 'left' : 'bottom'}
                                tabIndex={0}
                                triggerText={
                                  <span className="page_body">
                                    <FormattedMessage
                                      {...messages.maintenancePersonFTERateLabel}
                                    />
                                  </span>
                                }
                              >
                                <p className="slide_text">
                                  <FormattedMessage
                                    {...messages.maintenancePersonFTERateDescTooltip}
                                  />
                                </p>
                              </Tooltip>
                            </div>
                          </div>
                          {/*  Average Maintenance Personnel - Annual Cost Rate  Slider */}
                          <div className="bx--row expand-slider mar1">
                            <div className="bx--col-xlg-5 bx--col-lg-7 bx--col-md-3 bx--col-sm-2">
                              <TextInput
                                id="MFTER-Id"
                                hideLabel={true}
                                labelText={''}
                                min={MaintenanceAnnualFTERate.min}
                                max={MaintenanceAnnualFTERate.max}
                                value={input.maintenanceAnnualFTERate}
                                defaultValue={MaintenanceAnnualFTERate.default}
                                type={'number'}
                                invalid={
                                  invalidMaxMin.maintenanceAnnualFTERateMax ||
                                  invalidMaxMin.maintenanceAnnualFTERateMin
                                }
                                invalidText={
                                  invalidMaxMin.maintenanceAnnualFTERateMax ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_1}
                                      />
                                      {` ${MaintenanceAnnualFTERate.max}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : invalidMaxMin.maintenanceAnnualFTERateMin ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_3}
                                      />
                                      {` ${MaintenanceAnnualFTERate.min}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : null
                                }
                                onBlur={() => {
                                  null;
                                }}
                                onChange={(e) => {
                                  handleSliders(
                                    Number.parseInt(e.target.value),
                                    MaintenanceAnnualFTERate.max,
                                    MaintenanceAnnualFTERate.min,
                                    'maintenanceAnnualFTERate',
                                  );
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Maintenance annual FTE rate slider',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          </div>
                        </AccordionItem>
                        <AccordionItem
                          title={
                            <p className="page_body_2">
                              <FormattedMessage
                                {...messages.accordionTitle_2}
                              />
                            </p>
                          }
                        >
                          {/*  External Maintenance Vendor Spend */}
                          <div className="bx--row mar3 expand-description">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <p className="slide_text">
                                <FormattedMessage
                                  {...messages.accordionDesc_2}
                                />
                              </p>
                            </div>
                          </div>
                          <div className="bx--row mar3">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Tooltip
                                onChange={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'External Maintenance vendor spend tooltip',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={isSmall ? 'left' : 'bottom'}
                                tabIndex={0}
                                triggerText={
                                  <span className="page_body">
                                    <FormattedMessage
                                      {...messages.extMaintenanceVendorSpendLabel}
                                    />
                                  </span>
                                }
                              >
                                <p className="slide_text">
                                  <FormattedMessage
                                    {...messages.extMaintenanceVendorSpendDescTooltip}
                                  />
                                </p>
                              </Tooltip>
                            </div>
                          </div>
                          <div className="bx--row expand-slider">
                            <div className="bx--col-xlg-5 bx--col-lg-7 bx--col-md-3 bx--col-sm-2">
                              {/*  External Maintenance Vendor Spend Slider */}
                              <TextInput
                                id="EMVS-Id"
                                hideLabel={true}
                                labelText={''}
                                min={ExtMaintenanceVendSpend.min}
                                max={ExtMaintenanceVendSpend.max}
                                value={input.extMaintenanceVendSpend}
                                defaultValue={ExtMaintenanceVendSpend.default}
                                type={'number'}
                                onBlur={() => {
                                  null;
                                }}
                                invalid={
                                  invalidMaxMin.extMaintenanceVendSpendMax ||
                                  invalidMaxMin.extMaintenanceVendSpendMin
                                }
                                invalidText={
                                  invalidMaxMin.extMaintenanceVendSpendMax ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_1}
                                      />
                                      {` ${ExtMaintenanceVendSpend.max}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : invalidMaxMin.extMaintenanceVendSpendMin ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_3}
                                      />
                                      {` ${ExtMaintenanceVendSpend.min}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : null
                                }
                                onChange={(e) => {
                                  handleSliders(
                                    Number.parseInt(e.target.value),
                                    ExtMaintenanceVendSpend.max,
                                    ExtMaintenanceVendSpend.min,
                                    'extMaintenanceVendSpend',
                                  );

                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Downtime hours annual field',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          </div>
                          {/* % External Maintenance Vendor Spend Reduction */}
                          <div className="bx--row mar3">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Tooltip
                                onChange={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'External mainenance vendor spend reduction tooltip',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={'bottom'}
                                tabIndex={0}
                                triggerText={
                                  <span className="page_body">
                                    <FormattedMessage
                                      {...messages.extMaintVendorSpendReduceLabel}
                                    />
                                  </span>
                                }
                              >
                                <p className="slide_text">
                                  <FormattedMessage
                                    {...messages.extMaintVendorSpendReduceDescTooltip}
                                  />
                                </p>
                              </Tooltip>
                            </div>
                          </div>
                          {/* % External Maintenance Vendor Spend Reduction Slider */}
                          <div className="bx--row expand-slider">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Slider
                                id="EMVSR-Id"
                                min={ExtMaintenanceVendSpendReduce.min}
                                max={ExtMaintenanceVendSpendReduce.max}
                                inputType={'number'}
                                value={input.extMaintenanceVendSpendReduce}
                                invalid={
                                  invalidMaxMin.extMaintenanceVendSpendReduceMax ||
                                  invalidMaxMin.extMaintenanceVendSpendReduceMin
                                }
                                labelText={
                                  invalidMaxMin.extMaintenanceVendSpendReduceMax ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_1}
                                      />
                                      {` ${ExtMaintenanceVendSpendReduce.max}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : invalidMaxMin.extMaintenanceVendSpendReduceMin ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_3}
                                      />
                                      {` ${ExtMaintenanceVendSpendReduce.min}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : null
                                }
                                onBlur={() => {
                                  null;
                                }}
                                onChange={({ value }) => {
                                  handleSliders(
                                    value,
                                    ExtMaintenanceVendSpendReduce.max,
                                    ExtMaintenanceVendSpendReduce.min,
                                    'extMaintenanceVendSpendReduce',
                                  );

                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'External maintenance vendor spend reduction slider',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          </div>
                        </AccordionItem>
                        <AccordionItem
                          title={
                            <p className="page_body_2">
                              <FormattedMessage
                                {...messages.accordionTitle_3}
                              />
                            </p>
                          }
                        >
                          {/* Annual Downtime Hours */}
                          <div className="bx--row mar3 expand-description">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <p className="slide_text">
                                <FormattedMessage
                                  {...messages.accordionDesc_3}
                                />
                              </p>
                            </div>
                          </div>
                          <div className="bx--row mar3">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Tooltip
                                onChange={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Donwime hours annual tooltip',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={isSmall ? 'left' : 'bottom'}
                                tabIndex={0}
                                triggerText={
                                  <span className="page_body">
                                    <FormattedMessage
                                      {...messages.downTimeHoursAnnualLabel}
                                    />
                                  </span>
                                }
                              >
                                <p className="slide_text">
                                  <FormattedMessage
                                    {...messages.downTimeHoursAnnualDescTooltip}
                                  />
                                </p>
                              </Tooltip>
                            </div>
                          </div>
                          <div className="bx--row expand-slider">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              {/* Annual Downtime Hours Slider */}
                              <Slider
                                id="DT-Id"
                                min={DownTimeHours.min}
                                max={DownTimeHours.max}
                                value={input.downTimeHours}
                                inputType={'number'}
                                onBlur={() => {
                                  null;
                                }}
                                invalid={
                                  invalidMaxMin.downTimeHoursMax ||
                                  invalidMaxMin.downTimeHoursMin
                                }
                                labelText={
                                  invalidMaxMin.downTimeHoursMax ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_1}
                                      />
                                      {` ${DownTimeHours.max}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : invalidMaxMin.downTimeHoursMin ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_3}
                                      />
                                      {` ${DownTimeHours.min}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : null
                                }
                                onChange={({ value }) => {
                                  handleSliders(
                                    value,
                                    DownTimeHours.max,
                                    DownTimeHours.min,
                                    'downTimeHours',
                                  );

                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Downtime hours annual slider',
                                    location: 'ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          </div>
                          {/* % Downtime Reduction */}
                          <div className="bx--row mar3">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Tooltip
                                onChange={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Downtime reduction tooltip',
                                    location: 'ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={'bottom'}
                                tabIndex={0}
                                triggerText={
                                  <span className="page_body">
                                    <FormattedMessage
                                      {...messages.downTimeReduceLabel}
                                    />
                                  </span>
                                }
                              >
                                <p className="slide_text">
                                  <FormattedMessage
                                    {...messages.downTimeReduceDescTooltip}
                                  />
                                </p>
                              </Tooltip>
                            </div>
                          </div>
                          {/* % Downtime Reduction Slider */}
                          <div className="bx--row expand-slider">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Slider
                                id="DTR-Id"
                                min={DownTimeReduce.min}
                                max={DownTimeReduce.max}
                                inputType={'number'}
                                value={input.downTimeReduce}
                                invalid={
                                  invalidMaxMin.downTimeReduceMax ||
                                  invalidMaxMin.downTimeReduceMin
                                }
                                labelText={
                                  invalidMaxMin.downTimeReduceMax ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_1}
                                      />
                                      {` ${DownTimeReduce.max}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : invalidMaxMin.downTimeReduceMin ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_3}
                                      />
                                      {` ${DownTimeReduce.min}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : null
                                }
                                onBlur={() => {
                                  null;
                                }}
                                onChange={({ value }) => {
                                  handleSliders(
                                    value,
                                    DownTimeReduce.max,
                                    DownTimeReduce.min,
                                    'downTimeReduce',
                                  );

                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Downtime reduction slider',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          </div>
                          {/* Average Economic Cost of Downtime per hour */}
                          <div className="bx--row mar3">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Tooltip
                                onChange={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Downtime per hour cost tooltip',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={isSmall ? 'left' : 'bottom'}
                                tabIndex={0}
                                triggerText={
                                  <span className="page_body">
                                    <FormattedMessage
                                      {...messages.downTimePerHrCostLabel}
                                    />
                                  </span>
                                }
                              >
                                <p className="slide_text">
                                  <FormattedMessage
                                    {...messages.downTimePerHrCostDescTooltip}
                                  />
                                </p>
                              </Tooltip>
                            </div>
                          </div>
                          {/* Average Economic Cost of Downtime per hour Slider */}
                          <div className="bx--row expand-slider mar1">
                            <div className="bx--col-xlg-5 bx--col-lg-7 bx--col-md-3 bx--col-sm-2">
                              <TextInput
                                id="DTCPH-Id"
                                hideLabel={true}
                                labelText={''}
                                min={DownTimeCostPerHour.min}
                                max={DownTimeCostPerHour.max}
                                value={input.downTimeCostPerHour}
                                defaultValue={DownTimeCostPerHour.default}
                                type={'number'}
                                invalid={
                                  invalidMaxMin.downTimeCostPerHourMax ||
                                  invalidMaxMin.downTimeCostPerHourMin
                                }
                                invalidText={
                                  invalidMaxMin.downTimeCostPerHourMax ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_1}
                                      />
                                      {` ${DownTimeCostPerHour.max}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : invalidMaxMin.downTimeCostPerHourMin ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_3}
                                      />
                                      {` ${DownTimeCostPerHour.min}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : null
                                }
                                onBlur={() => {
                                  null;
                                }}
                                onChange={(e) => {
                                  handleSliders(
                                    Number.parseInt(e.target.value),
                                    DownTimeCostPerHour.max,
                                    DownTimeCostPerHour.min,
                                    'downTimeCostPerHour',
                                  );
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Downtime per hour cost field',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          </div>
                        </AccordionItem>
                        <AccordionItem
                          title={
                            <p className="page_body_2">
                              <FormattedMessage
                                {...messages.accordionTitle_5}
                              />
                            </p>
                          }
                        >
                          {/*  Annual MRO Spend Tooltip */}
                          <div className="bx--row mar3 expand-description">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <p className="slide_text">
                                <FormattedMessage
                                  {...messages.accordionDesc_5}
                                />
                              </p>
                            </div>
                          </div>
                          <div className="bx--row mar3">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Tooltip
                                onChange={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Annual MRO spend tooltip',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={isSmall ? 'left' : 'bottom'}
                                tabIndex={0}
                                triggerText={
                                  <span className="page_body">
                                    <FormattedMessage
                                      {...messages.annualMroSpendLabel}
                                    />
                                  </span>
                                }
                              >
                                <p className="slide_text">
                                  <FormattedMessage
                                    {...messages.annualMroSpendDescTooltip}
                                  />
                                </p>
                              </Tooltip>
                            </div>
                          </div>
                          <div className="bx--row expand-slider">
                            <div className="bx--col-xlg-5 bx--col-lg-7 bx--col-md-3 bx--col-sm-2">
                              {/*  Annual MRO Spend Slider */}
                              <TextInput
                                id="MRO-Id"
                                hideLabel={true}
                                labelText={''}
                                min={MROSpend.min}
                                max={MROSpend.max}
                                value={input.mroSpend}
                                defaultValue={MROSpend.default}
                                type={'number'}
                                onBlur={() => {
                                  null;
                                }}
                                invalid={
                                  invalidMaxMin.mroSpendMax ||
                                  invalidMaxMin.mroSpendMin
                                }
                                invalidText={
                                  invalidMaxMin.mroSpendMax ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_1}
                                      />
                                      {` ${MROSpend.max}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : invalidMaxMin.mroSpendMin ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_3}
                                      />
                                      {` ${MROSpend.min}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : null
                                }
                                onChange={(e) => {
                                  handleSliders(
                                    Number.parseInt(e.target.value),
                                    MROSpend.max,
                                    MROSpend.min,
                                    'mroSpend',
                                  );

                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Annual MRO spend field',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          </div>
                          {/* % MRO Spend Reduction Tooltip */}
                          <div className="bx--row mar3">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Tooltip
                                onChange={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'MRO overspend reduction tooltip',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={'bottom'}
                                tabIndex={0}
                                triggerText={
                                  <span className="page_body">
                                    <FormattedMessage
                                      {...messages.mroOverSpendReduceLabel}
                                    />
                                  </span>
                                }
                              >
                                <p className="slide_text">
                                  <FormattedMessage
                                    {...messages.mroOverSpendReduceDescTooltip}
                                  />
                                </p>
                              </Tooltip>
                            </div>
                          </div>
                          {/* % MRO Spend Reduction Slider */}
                          <div className="bx--row expand-slider">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Slider
                                id="MROR-Id"
                                min={MroOverSpendReduce.min}
                                max={MroOverSpendReduce.max}
                                inputType={'number'}
                                value={input.mroOverSpendReduce}
                                invalid={
                                  invalidMaxMin.mroOverSpendReduceMax ||
                                  invalidMaxMin.mroOverSpendReduceMin
                                }
                                labelText={
                                  invalidMaxMin.mroOverSpendReduceMax ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_1}
                                      />
                                      {` ${MroOverSpendReduce.max}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : invalidMaxMin.mroOverSpendReduceMin ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_3}
                                      />
                                      {` ${MroOverSpendReduce.min}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : null
                                }
                                onBlur={() => {
                                  null;
                                }}
                                onChange={({ value }) => {
                                  handleSliders(
                                    value,
                                    MroOverSpendReduce.max,
                                    MroOverSpendReduce.min,
                                    'mroOverSpendReduce',
                                  );

                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'MRO overspend reduction slider',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          </div>
                        </AccordionItem>
                        <AccordionItem
                          title={
                            <p className="page_body_2">
                              <FormattedMessage
                                {...messages.accordionTitle_4}
                              />
                            </p>
                          }
                        >
                          {/* Annual CapEx Budget to Replace/Overhaul Assets */}
                          <div className="bx--row mar3 expand-description">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <p className="slide_text">
                                <FormattedMessage
                                  {...messages.accordionDesc_4}
                                />
                              </p>
                            </div>
                          </div>
                          <div className="bx--row mar3">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Tooltip
                                onChange={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Replace assets budget tooltip',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={isSmall ? 'left' : 'bottom'}
                                tabIndex={0}
                                triggerText={
                                  <span className="page_body">
                                    <FormattedMessage
                                      {...messages.replaceAssetsBudgetLabel}
                                    />
                                  </span>
                                }
                              >
                                <p className="slide_text">
                                  <FormattedMessage
                                    {...messages.replaceAssetsBudgetDescTooltip}
                                  />
                                </p>
                              </Tooltip>
                            </div>
                          </div>
                          <div className="bx--row expand-slider">
                            <div className="bx--col-xlg-5 bx--col-lg-7 bx--col-md-3 bx--col-sm-2">
                              {/*  Annual CapEx Budget to Replace/Overhaul Assets Slider */}
                              <TextInput
                                id="RAB-Id"
                                hideLabel={true}
                                labelText={''}
                                min={ReplaceAssetBudget.min}
                                max={ReplaceAssetBudget.max}
                                value={input.replaceAssetBudget}
                                defaultValue={ReplaceAssetBudget.default}
                                type={'number'}
                                onBlur={() => {
                                  null;
                                }}
                                invalid={
                                  invalidMaxMin.replaceAssetBudgetMax ||
                                  invalidMaxMin.replaceAssetBudgetMin
                                }
                                invalidText={
                                  invalidMaxMin.replaceAssetBudgetMax ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_1}
                                      />
                                      {` ${ReplaceAssetBudget.max}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : invalidMaxMin.replaceAssetBudgetMin ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_3}
                                      />
                                      {` ${ReplaceAssetBudget.min}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : null
                                }
                                onChange={(e) => {
                                  handleSliders(
                                    Number.parseInt(e.target.value),
                                    ReplaceAssetBudget.max,
                                    ReplaceAssetBudget.min,
                                    'replaceAssetBudget',
                                  );

                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Replace assets budget field',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          </div>
                          {/* % Reduction in CapEx Budget Need  */}
                          <div className="bx--row mar3">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Tooltip
                                onChange={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Assets life extension tooltip',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={'bottom'}
                                tabIndex={0}
                                triggerText={
                                  <span className="page_body">
                                    <FormattedMessage
                                      {...messages.lifeAssetsExtLabel}
                                    />
                                  </span>
                                }
                              >
                                <p className="slide_text">
                                  <FormattedMessage
                                    {...messages.lifeAssetsExtDescTooltip}
                                  />
                                </p>
                              </Tooltip>
                            </div>
                          </div>
                          {/* % Reduction in CapEx Budget Need Slider */}
                          <div className="bx--row expand-slider">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Slider
                                id="OPH-Id"
                                min={AssetsLifeExtension.min}
                                max={AssetsLifeExtension.max}
                                inputType={'number'}
                                value={input.assetsLifeExtension}
                                invalid={
                                  invalidMaxMin.assetsLifeExtensionMax ||
                                  invalidMaxMin.assetsLifeExtensionMin
                                }
                                labelText={
                                  invalidMaxMin.assetsLifeExtensionMax ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_1}
                                      />
                                      {` ${AssetsLifeExtension.max}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : invalidMaxMin.assetsLifeExtensionMin ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_3}
                                      />
                                      {` ${AssetsLifeExtension.min}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : null
                                }
                                onBlur={() => {
                                  null;
                                }}
                                onChange={({ value }) => {
                                  handleSliders(
                                    value,
                                    AssetsLifeExtension.max,
                                    AssetsLifeExtension.min,
                                    'assetsLifeExtension',
                                  );

                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Assets life extension slider',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          </div>
                        </AccordionItem>
                        <AccordionItem
                          title={
                            <p className="page_body_2">
                              <FormattedMessage
                                {...messages.accordionTitle_6}
                              />
                            </p>
                          }
                        >
                          {/* Annual Cost to Support Current Asset Mgmt Tools Tooltip */}
                          <div className="bx--row mar3 expand-description">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <p className="slide_text">
                                <FormattedMessage
                                  {...messages.accordionDesc_6}
                                />
                              </p>
                            </div>
                          </div>
                          <div className="bx--row mar3">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Tooltip
                                onChange={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Asset Mgmt tool cost tooltip',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={isSmall ? 'left' : 'bottom'}
                                tabIndex={0}
                                triggerText={
                                  <span className="page_body">
                                    <FormattedMessage
                                      {...messages.asstMgmtToolsCostLabel}
                                    />
                                  </span>
                                }
                              >
                                <p className="slide_text">
                                  <FormattedMessage
                                    {...messages.asstMgmtToolsCostDescTooltip}
                                  />
                                </p>
                              </Tooltip>
                            </div>
                          </div>
                          <div className="bx--row expand-slider">
                            <div className="bx--col-xlg-6 bx--col-lg-7 bx--col-md-3 bx--col-sm-2">
                              {/* Annual Cost to Support Current Asset Mgmt Tools Slider */}
                              <TextInput
                                id="AMTC-Id"
                                hideLabel={true}
                                labelText={''}
                                min={AssetMgmtToolCost.min}
                                max={AssetMgmtToolCost.max}
                                value={input.assetMgmtToolCost}
                                defaultValue={AssetMgmtToolCost.default}
                                type={'number'}
                                onBlur={() => {
                                  null;
                                }}
                                invalid={
                                  invalidMaxMin.assetMgmtToolCostMax ||
                                  invalidMaxMin.assetMgmtToolCostMin
                                }
                                invalidText={
                                  invalidMaxMin.assetMgmtToolCostMax ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_1}
                                      />
                                      {` ${AssetMgmtToolCost.max}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : invalidMaxMin.assetMgmtToolCostMin ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_3}
                                      />
                                      {` ${AssetMgmtToolCost.min}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : null
                                }
                                onChange={(e) => {
                                  handleSliders(
                                    Number.parseInt(e.target.value),
                                    AssetMgmtToolCost.max,
                                    AssetMgmtToolCost.min,
                                    'assetMgmtToolCost',
                                  );

                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Asset Mgmt tool cost field',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          </div>
                          {/* % Reduction of Asset Mgmt Tools Support Cost Tooltip */}
                          <div className="bx--row mar3">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Tooltip
                                onChange={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Asset Mgmt tool cost reduction tooltip',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={'bottom'}
                                tabIndex={0}
                                triggerText={
                                  <span className="page_body">
                                    <FormattedMessage
                                      {...messages.asstMgmtToolsCostReduceLabel}
                                    />
                                  </span>
                                }
                              >
                                <p className="slide_text">
                                  <FormattedMessage
                                    {...messages.asstMgmtToolsCostReduceDescTooltip}
                                  />
                                </p>
                              </Tooltip>
                            </div>
                          </div>
                          {/* % Reduction of Asset Mgmt Tools Support Cost Slider */}
                          <div className="bx--row expand-slider">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <Slider
                                id="OPH-Id"
                                min={AssetMgmtCostReduce.min}
                                max={AssetMgmtCostReduce.max}
                                inputType={'number'}
                                value={input.assetMgmtCostReduce}
                                invalid={
                                  invalidMaxMin.assetMgmtCostReduceMax ||
                                  invalidMaxMin.assetMgmtCostReduceMin
                                }
                                labelText={
                                  invalidMaxMin.assetMgmtCostReduceMax ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_1}
                                      />
                                      {` ${AssetMgmtCostReduce.max}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : invalidMaxMin.assetMgmtCostReduceMin ? (
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.invalidVmText_3}
                                      />
                                      {` ${AssetMgmtCostReduce.min}`}
                                      <FormattedMessage
                                        {...messages.invalidVmText_2}
                                      />
                                    </span>
                                  ) : null
                                }
                                onBlur={() => {
                                  null;
                                }}
                                onChange={({ value }) => {
                                  handleSliders(
                                    value,
                                    AssetMgmtCostReduce.max,
                                    AssetMgmtCostReduce.min,
                                    'assetMgmtCostReduce',
                                  );

                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Asset Mgmt tool cost reduction slider',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          </div>
                        </AccordionItem>
                      </Accordion>
                    </div>
                  </div>
                </div>
                <div id="footnote" className="div-only-lg mar2">
                  <div className="bx--row">
                    <div className="bx--col-lg-16">
                      <Footnote numNote={1} />
                      <div className="footnote_text">
                        <FormattedMessage {...messages.disclaimer_text} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Navigation displaying results  */}
              <div className="bx--col-lg-1"></div>
              <div className="bx--col-lg-6 bx--col-md-16 bx--col-sm-16">
                <div className="bx--row" id="stickyNav">
                  <div className="MAS_ROI__sticky-nav price_table_comp">
                    {/* Table with results  */}
                    <div id="graphSummary">
                      <div style={{ backgroundColor: '#f4f4f4' }}>
                        <p
                          id="widget.estimated-cost"
                          className="MAS_ROI__ibm-type-e estmt-cost price_table_comp__bodycopy"
                        >
                          <FormattedMessage {...messages.stickyNavTitle} />{' '}
                          <sup className="MAS_ROI__sup">1</sup>
                        </p>
                        <p className="padding_1 price_table_comp__price padding_24_1">
                          {`${estimatedSavings}*`}
                        </p>
                        <div>
                          <DonutChart
                            data={state.data}
                            options={state.options}
                          ></DonutChart>
                        </div>
                      </div>
                    </div>
                    <div className="mar4">
                      <div id="tableSummary">
                        <table className="cds--data-table cds--data-table--sm">
                          <thead>
                            <tr>
                              <th scope="col" colSpan={2}>
                                <div className="price_table_comp__heading">
                                  <FormattedMessage {...messages.category} />
                                </div>
                              </th>
                              <th></th>
                              <th scope="col">
                                <div className="price_table_comp__heading">
                                  <FormattedMessage {...messages.benefit} />
                                  {` (${currency})`}
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody aria-live="polite">
                            <tr>
                              <td colSpan={2}>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.maintenancePersonProdTable}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">{`${removeSymbol(
                                  currency,
                                  MaintenanceTotal,
                                )}`}</div>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan={2}>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.maintenanceCostReduceTable}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">{`${removeSymbol(
                                  currency,
                                  ReduceExtMaintTotal,
                                )}`}</div>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan={2}>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.assetUtilPerformanceTable}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">{`${removeSymbol(
                                  currency,
                                  ReduceDowntimeCostTotal,
                                )}`}</div>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan={2}>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.inventoryCostReduceTable}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">{`${removeSymbol(
                                  currency,
                                  IncreaseMROTotal,
                                )}`}</div>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan={2}>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.assetCapExOptTable}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">{`${removeSymbol(
                                  currency,
                                  ExtendAssetLifeTotal,
                                )}`}</div>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan={2}>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.itCostReduceTable}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">{`${removeSymbol(
                                  currency,
                                  AssetMgmtToolsTotal,
                                )}`}</div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="bx--row mar3_b">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                          <div id="price_toggle" className="padding_24_2">
                            <ButtonSet
                              className="MAS_ROI__stack-button"
                              stacked
                            >
                              {pdfExport ? (
                                <Button
                                  kind="secondary"
                                  renderIcon={DocumentPdf32}
                                  className="marright"
                                >
                                  <PDFDownloadLink
                                    document={pdfObject}
                                    fileName={
                                      pdfThanksContent
                                        ? pdfThanksContent[0].value
                                        : ''
                                    }
                                    className="pdflink"
                                  >
                                    <p
                                      className="price_table_comp__disclaimer pdfText"
                                      onClick={() => {
                                        setPdfExport(false);
                                        setLoadInline(false);
                                        changeIsPdf(false);
                                        segmentTracking('CTA Clicked', {
                                          pageTitle: 'MAS ROI Page',
                                          object: 'MAS ROI Page',
                                          resultValue: 'CTA clicked',
                                          CTA: 'Download Pdf',
                                          location: 'MAS ROI Estimator',
                                          action: '',
                                          field: '',
                                        });
                                      }}
                                    >
                                      {pdfThanksContent
                                        ? pdfThanksContent[0].group
                                        : ''}
                                    </p>
                                  </PDFDownloadLink>
                                </Button>
                              ) : (
                                <Button
                                  renderIcon={!isPdf ? PdfReference16 : null}
                                  kind="secondary"
                                  className="marright"
                                  onClick={() => {
                                    setExportPdfStatus(true);
                                    changeIsPdf(true);
                                    segmentTracking('CTA Clicked', {
                                      pageTitle: 'MAS ROI Page',
                                      object: 'MAS ROI Page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Export to PDF',
                                      location: 'MAS ROI Estimator',
                                      action: '',
                                      field: '',
                                    });
                                  }}
                                  disabled={isPdf}
                                >
                                  {loadInline ? (
                                    <InlineLoading
                                      status={loaderStatus}
                                      description={loaderDes}
                                    />
                                  ) : (
                                    <FormattedMessage
                                      {...messages.export_to_pdf}
                                    />
                                  )}
                                </Button>
                              )}
                              <Button
                                id="discuss_more_btn"
                                onClick={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'MAS ROI Page',
                                    object: 'MAS ROI Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Discuss More',
                                    location: 'MAS ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                  initUrxForm(
                                    environment,
                                    URX_FORM.INSTANCE_ID,
                                    URX_FORM.FORM_ID,
                                    urxlang as IBMLocale,
                                    companyId,
                                  );
                                  setDrawerOpen(true);
                                  setCookie();
                                }}
                              >
                                <FormattedMessage
                                  {...messages.discussMoreBtn}
                                />
                              </Button>
                            </ButtonSet>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div id="footnote" className="div-only-media">
                    <div className="bx--row mar4 mar2">
                      <div className="bx--col-lg-9">
                        <div>
                          <Footnote numNote={1} />
                        </div>
                        <div className="footnote_text">
                          <FormattedMessage {...messages.disclaimer_text} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FormModal
        pageTitle="MAS ROI Page"
        object="MAS ROI Page"
        productTitle={'MAS ROI price estimator'}
        pageUrl={''}
        location={'MAS ROI Estimator'}
        bpName={bpName}
        logo={logo}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        id={'urx-form-maximo'}
        formId={URX_FORM.FORM_ID}
        lang={lang}
      ></FormModal>
    </div>
  );
};

export default MASROIEstimator;
